import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { Pencil, Trash2 } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { DetailGrid } from '@/components/backoffice/DetailGrid'
import { Button } from '@/components/ui/button'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import type { NotarizationTypeSummary } from '@/types'
import { formatCents } from '@/lib/utils'

type Props = {
  type: NotarizationTypeSummary
}

export default function NotarizationTypesShow({ type }: Props) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  return (
    <BackofficeLayout title={type.name} breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Notarization Types', href: '/backoffice/notarization_types' }, { label: type.name }]}>
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">{type.name}</h1>
          <p className="text-sm text-muted-foreground">{type.code}</p>
        </div>

        <div className="flex gap-2">
          <Button size="sm" variant="outline" asChild className="h-8 text-[13px]">
            <Link href={`/backoffice/notarization_types/${type.id}/edit`}><Pencil className="mr-1.5 h-3 w-3" />Edit</Link>
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowDeleteDialog(true)} className="h-8 text-[13px] text-destructive hover:text-destructive">
            <Trash2 className="mr-1.5 h-3 w-3" />Delete
          </Button>
        </div>

        <DetailGrid items={[
          { label: 'Jurisdiction', value: type.jurisdiction_label || '\u2014' },
          { label: 'Service Fee', value: formatCents(type.service_fee_cents) },
          { label: 'Gov Fee', value: formatCents(type.gov_fee_cents) },
          { label: 'Slot Duration', value: `${type.slot_duration_minutes} minutes` },
          { label: 'Processing', value: type.processing_days_min && type.processing_days_max ? `${type.processing_days_min}\u2013${type.processing_days_max} days` : '\u2014' },
        ]} />

        {/* Session Modes */}
        <div className="py-3 border-b border-border/40">
          <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Session Modes</p>
          <div className="flex gap-2">
            {type.session_modes.map((mode) => (
              <Badge key={mode} className="bg-secondary text-secondary-foreground font-normal text-xs capitalize">{mode}</Badge>
            ))}
          </div>
        </div>

        {type.description && (
          <div className="py-3 border-b border-border/40">
            <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Description</p>
            <p className="text-sm">{type.description}</p>
          </div>
        )}

        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {type.name}?</AlertDialogTitle>
              <AlertDialogDescription>This will permanently delete this notarization type. Types with existing notarizations cannot be deleted.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => router.delete(`/backoffice/notarization_types/${type.id}`)}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </BackofficeLayout>
  )
}
